import { FC, useState } from 'react';
import { useStore } from '@nanostores/react';
import {
  $competitionTypes,
  $manageCompetitionTypesModalIsOpen,
  addCompetitionType,
  updateCompetitionType,
  removeCompetitionType,
  CompetitionType,
} from '../store/planner';
import {
  ModalOverlay,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  CloseButton,
  ModalContent,
  ModalFooter,
  CancelButton,
  FormContainer,
  FormControl,
  FieldLabel,
} from './PlanFormPage.style';
import {
  XMarkIcon,
  PencilIcon,
  TrashIcon,
  CheckIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { Button as AppButton } from '../components/Button';
import { v4 as uuidv4 } from 'uuid';
import styled from 'styled-components';
import {
  white,
  yellow,
  darkBlueBackground,
  darkBlueBorder,
} from '../components/colors';
import { Button, Modal } from 'antd';
import { TextInput } from '../components/TextInput';

// --- Styled Components ---

const CompetitionTypesListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const CompetitionTypeRow = styled.div<{ $isEditing?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background-color: ${(props) =>
    props.$isEditing ? darkBlueBackground : darkBlueBorder};
  border: 2px solid
    ${(props) => (props.$isEditing ? yellow : 'rgba(255, 255, 255, 0.2)')};
  border-radius: 8px;
  transition: border-color 0.2s;

  &:hover {
    border-color: ${(props) =>
      props.$isEditing ? yellow : 'rgba(255, 255, 255, 0.4)'};
  }
`;

const ActionButton = styled(Button)`
  &.ant-btn {
    background-color: transparent;
    border: 1px solid rgba(255, 255, 255, 0.3);
    color: ${white};
    font-weight: bold;
    width: 40px;
    height: 40px;
    padding: 0;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      background-color: rgba(255, 255, 255, 0.1) !important;
      border-color: ${white} !important;
      color: ${white} !important;
    }
  }
`;

const ActionButtonDanger = styled(ActionButton)`
  &.ant-btn {
    border-color: #ef5350;
    color: #ef5350;

    &:hover {
      background-color: rgba(239, 83, 80, 0.15) !important;
      border-color: #ef5350 !important;
      color: #ef5350 !important;
    }
  }
`;

const ActionButtonSuccess = styled(ActionButton)`
  &.ant-btn {
    border-color: #66bb6a;
    color: #66bb6a;

    &:hover {
      background-color: rgba(102, 187, 106, 0.15) !important;
      border-color: #66bb6a !important;
      color: #66bb6a !important;
    }
  }
`;

const AddCompetitionTypeForm = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-end;
  width: 100%;
  padding: 16px;
  background-color: rgba(255, 255, 255, 0.05);
  border: 2px dashed rgba(255, 255, 255, 0.2);
  border-radius: 8px;
`;

const AddCompetitionTypeFormInputs = styled.div`
  flex: 1;
  display: flex;
  gap: 12px;
  align-items: flex-end;
`;

const CompetitionTypeNameText = styled.div`
  font-weight: bold;
  font-size: 16px;
  color: ${white};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 8px;
`;

const CompetitionTypeCount = styled.span`
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;
  font-weight: bold;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: rgba(255, 255, 255, 0.5);
  font-size: 16px;
`;

const EditInputRow = styled.div`
  flex: 1;
  display: flex;
  gap: 12px;
  align-items: center;
`;

// --- Component ---

export const ManageCompetitionTypesModal: FC = () => {
  const isOpen = useStore($manageCompetitionTypesModalIsOpen);
  const competitionTypes = useStore($competitionTypes);

  // Add form state
  const [newName, setNewName] = useState('');

  // Edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');

  // Delete confirmation
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const handleClose = () => {
    $manageCompetitionTypesModalIsOpen.set(false);
    resetAddForm();
    setEditingId(null);
  };

  const resetAddForm = () => {
    setNewName('');
  };

  const handleAdd = () => {
    if (!newName.trim()) return;

    addCompetitionType({
      id: uuidv4(),
      name: newName.trim(),
    });
    resetAddForm();
  };

  const handleStartEdit = (competitionType: CompetitionType) => {
    setEditingId(competitionType.id);
    setEditName(competitionType.name);
  };

  const handleSaveEdit = () => {
    if (!editingId || !editName.trim()) return;

    updateCompetitionType({
      id: editingId,
      name: editName.trim(),
    });
    setEditingId(null);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
  };

  const handleDeleteConfirm = (id: string) => {
    setDeleteConfirmId(id);
  };

  const handleDeleteExecute = () => {
    if (deleteConfirmId) {
      removeCompetitionType(deleteConfirmId);
      setDeleteConfirmId(null);
      if (editingId === deleteConfirmId) {
        setEditingId(null);
      }
    }
  };

  // Sort alphabetically
  const sortedCompetitionTypes = [...competitionTypes].sort((a, b) =>
    a.name.localeCompare(b.name),
  );

  const deleteTargetCompetitionType = competitionTypes.find(
    (t) => t.id === deleteConfirmId,
  );

  return (
    <>
      <ModalOverlay $isOpen={isOpen}>
        <ModalContainer>
          <ModalHeader>
            <ModalTitle>Manage Competition Types</ModalTitle>
            <CloseButton onClick={handleClose}>
              <div>
                <XMarkIcon
                  style={{
                    color: '#fff',
                    height: '30px',
                    width: '30px',
                  }}
                  fontSize={30}
                />
              </div>
            </CloseButton>
          </ModalHeader>

          <ModalContent>
            <FormContainer>
              {/* Add new competition type form */}
              <FormControl>
                <FieldLabel>Add New Competition Type</FieldLabel>
                <AddCompetitionTypeForm>
                  <AddCompetitionTypeFormInputs>
                    <div style={{ flex: 1 }}>
                      <TextInput
                        disableForceUppercase={true}
                        label="Competition Type Name"
                        placeholder="Enter competition type name..."
                        value={newName}
                        onChange={setNewName}
                      />
                    </div>
                  </AddCompetitionTypeFormInputs>
                  <AppButton
                    style={{ margin: 0, height: '56px', fontSize: '16px' }}
                    large={true}
                    onClick={handleAdd}
                  >
                    Add
                  </AppButton>
                </AddCompetitionTypeForm>
              </FormControl>

              {/* Competition type list */}
              <FormControl>
                <SectionHeader>
                  <FieldLabel>Competition Types</FieldLabel>
                  <CompetitionTypeCount>
                    {competitionTypes.length} competition types
                  </CompetitionTypeCount>
                </SectionHeader>

                {sortedCompetitionTypes.length === 0 ? (
                  <EmptyState>
                    No competition types added yet. Use the form above to add
                    competition types.
                  </EmptyState>
                ) : (
                  <CompetitionTypesListContainer>
                    {sortedCompetitionTypes.map((competitionType) => (
                      <CompetitionTypeRow
                        key={competitionType.id}
                        $isEditing={editingId === competitionType.id}
                      >
                        {editingId === competitionType.id ? (
                          // Edit mode
                          <>
                            <EditInputRow>
                              <div style={{ flex: 1 }}>
                                <TextInput
                                  disableForceUppercase={true}
                                  label=""
                                  placeholder="Competition type name..."
                                  value={editName}
                                  onChange={setEditName}
                                />
                              </div>
                            </EditInputRow>
                            <ActionButtonSuccess onClick={handleSaveEdit}>
                              <CheckIcon style={{ width: 20, height: 20 }} />
                            </ActionButtonSuccess>
                            <ActionButton onClick={handleCancelEdit}>
                              <XCircleIcon style={{ width: 20, height: 20 }} />
                            </ActionButton>
                          </>
                        ) : (
                          // View mode
                          <>
                            <CompetitionTypeNameText>
                              {competitionType.name}
                            </CompetitionTypeNameText>
                            <ActionButton
                              onClick={() => handleStartEdit(competitionType)}
                            >
                              <PencilIcon style={{ width: 18, height: 18 }} />
                            </ActionButton>
                            <ActionButtonDanger
                              onClick={() =>
                                handleDeleteConfirm(competitionType.id)
                              }
                            >
                              <TrashIcon style={{ width: 18, height: 18 }} />
                            </ActionButtonDanger>
                          </>
                        )}
                      </CompetitionTypeRow>
                    ))}
                  </CompetitionTypesListContainer>
                )}
              </FormControl>
            </FormContainer>
          </ModalContent>

          <ModalFooter>
            <div style={{ flex: 1 }}></div>
            <CancelButton onClick={handleClose}>Close</CancelButton>
          </ModalFooter>
        </ModalContainer>
      </ModalOverlay>

      {/* Delete confirmation modal */}
      <Modal
        title="Delete Competition Type"
        open={deleteConfirmId !== null}
        onOk={handleDeleteExecute}
        onCancel={() => setDeleteConfirmId(null)}
        okText="Delete"
        cancelText="Cancel"
        okButtonProps={{
          danger: true,
          style: { fontWeight: 'bold' },
        }}
      >
        <p>
          Are you sure you want to delete{' '}
          <strong>
            {deleteTargetCompetitionType
              ? deleteTargetCompetitionType.name
              : 'this competition type'}
          </strong>
          ?
        </p>
        <p>This action cannot be undone.</p>
      </Modal>
    </>
  );
};
